import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { User } from '@shess/schemas'
import { useTRPC } from '../trpc.ts'
import { PuzzleBoard } from './PuzzleBoard.tsx'
import { StatsBar } from './StatsBar.tsx'

const RUN_LENGTHS = [5, 10, 20]

export function RunView({ user }: { user: User }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [length, setLength] = useState(10)

  const run = useQuery(trpc.run.current.queryOptions())
  const stats = useQuery(trpc.stats.me.queryOptions())

  const refresh = () => {
    void queryClient.invalidateQueries(trpc.run.pathFilter())
    void queryClient.invalidateQueries(trpc.stats.pathFilter())
  }

  const startRun = useMutation(trpc.run.start.mutationOptions({ onSuccess: refresh }))
  const abandonRun = useMutation(trpc.run.abandon.mutationOptions({ onSuccess: refresh }))
  const dismissRun = useMutation(trpc.run.dismiss.mutationOptions({ onSuccess: refresh }))
  const state = run.data

  return (
    <div className="flex min-h-dvh flex-col">
      <StatsBar user={user} stats={stats.data} />

      <main className="flex flex-1 flex-col items-center justify-center gap-6 p-5">
        {run.isPending ? <p className="text-stone-500">Loading…</p> : null}

        {run.error ? (
          <ErrorNote message={run.error.message} />
        ) : startRun.error ? (
          <ErrorNote message={startRun.error.message} />
        ) : null}

        {!run.isPending && !state ? (
          <div className="space-y-5 text-center">
            <div className="space-y-1">
              <h2 className="text-xl font-medium">Ready for a run?</h2>
              <p className="text-sm text-stone-400">
                Puzzles are picked near your rating, easiest first.
              </p>
            </div>

            <div className="flex items-center justify-center gap-2">
              {RUN_LENGTHS.map((option) => (
                <button
                  key={option}
                  onClick={() => setLength(option)}
                  className={`rounded-lg border px-4 py-2 text-sm transition ${
                    option === length
                      ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300'
                      : 'border-stone-700 text-stone-400 hover:border-stone-500'
                  }`}
                >
                  {option} puzzles
                </button>
              ))}
            </div>

            <button
              onClick={() => startRun.mutate({ length })}
              disabled={startRun.isPending}
              className="rounded-lg bg-emerald-600 px-6 py-2.5 font-medium text-white transition hover:bg-emerald-500 disabled:opacity-40"
            >
              {startRun.isPending ? 'Dealing…' : 'Start'}
            </button>
          </div>
        ) : null}

        {state && state.puzzle && state.attemptId ? (
          <>
            <div className="flex items-center gap-3 text-sm text-stone-400">
              <span className="tabular-nums">
                Puzzle {state.run.currentIndex + 1} of {state.run.length}
              </span>
              <span className="text-stone-600">·</span>
              <span className="tabular-nums">rating {state.puzzle.rating}</span>
              {state.puzzle.playerMoveCount > 1 ? (
                <>
                  <span className="text-stone-600">·</span>
                  <span>{state.puzzle.playerMoveCount} moves</span>
                </>
              ) : null}
            </div>

            <PuzzleBoard
              key={state.attemptId}
              puzzle={state.puzzle}
              attemptId={state.attemptId}
              onAdvance={refresh}
            />

            <button
              onClick={() => abandonRun.mutate()}
              className="text-xs text-stone-600 underline-offset-4 transition hover:text-stone-400 hover:underline"
            >
              End run
            </button>
          </>
        ) : null}

        {state && !state.puzzle ? (
          <div className="space-y-5 text-center">
            <div className="space-y-1">
              <h2 className="text-xl font-medium">Run complete</h2>
              <p className="text-3xl font-semibold tabular-nums">
                {state.run.correctCount}
                <span className="text-stone-500">/{state.run.length}</span>
              </p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <button
                onClick={() => startRun.mutate({ length })}
                disabled={startRun.isPending}
                className="rounded-lg bg-emerald-600 px-6 py-2.5 font-medium text-white transition hover:bg-emerald-500 disabled:opacity-40"
              >
                Another run
              </button>
              <button
                onClick={() => dismissRun.mutate()}
                className="text-xs text-stone-600 underline-offset-4 transition hover:text-stone-400 hover:underline"
              >
                Back to start
              </button>
            </div>
          </div>
        ) : null}
      </main>
    </div>
  )
}

function ErrorNote({ message }: { message: string }) {
  return (
    <p className="max-w-md rounded-lg border border-rose-900/60 bg-rose-950/40 px-4 py-3 text-center text-sm text-rose-300">
      {message}
    </p>
  )
}
